import React, { useEffect, useState } from 'react'
import {
  List,
  Datagrid,
  TextField,
  useTranslate,
  useResourceContext,
  useResourceDefinitions,
  useListController,
  useStore,
  FunctionField,
  DateField,
  useRefresh,
} from 'react-admin'
import { Container, Chip } from '@mui/material'
import { Archive } from '@mui/icons-material'
import { CardCommon } from 'src/components/uiElements'
import { ListViewActionsButtons } from 'src/components/uiElements/buttons/ListViewActionsButtons'
import { ListViewTopToolbar } from 'src/components/uiElements/CRUD/ListViewTopToolbar'
import { DeleteButtonWithConfirmation } from 'src/components/uiElements/buttons/DeleteButtonWithConfirmation'
import { getJWT } from 'src/helper/localStore/getJWT'

const ArchivePromotionButton = ({ record, resource, name, genderElement }) => {
  const refresh = useRefresh()
  const translate = useTranslate()

  const handleArchive = async () => {
    const url = `${process.env.REACT_APP_URL_RESERVATION}/promotion/${record.id}/archive`
    
    try {
      const response = await fetch(url, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'authentication-token': getJWT(),
        },
        body: JSON.stringify({}),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Error archivando la promoción')
      }

      refresh()
    } catch (error) {
      console.error('Error archivando promoción:', error)
      throw error
    }
  }

  return (
    <DeleteButtonWithConfirmation
      record={record}
      resource={resource}
      name={name}
      genderElement={genderElement}
      handleDeleteFunction={handleArchive}
      label=""
      title={translate('ra.action.archive')}
      startIcon={<Archive />}
      sx={{
        color: '#1976d2',
        '&:hover': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
        },
      }}
    />
  )
}

export const PromotionList = (props) => {
  const translate = useTranslate()
  const { setSort, isFetching } = useListController()
  const [isSorting, setIsSorting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [permissionsStore, setPermissionsStore] = useStore('currentResource', '{}')
  const [permissions, setPermissions] = useState(null)
  const propsResource = useResourceDefinitions()
  const resourceName = useResourceContext()

  useEffect(() => {
    setPermissionsStore(JSON.stringify(propsResource[resourceName]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propsResource, resourceName])

  useEffect(() => {
    if (permissionsStore) {
      setPermissions(JSON.parse(permissionsStore))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permissionsStore])

  useEffect(() => {
    if (isSorting && isFetching) {
      setIsLoading(true)
    } else if (isLoading && isSorting && !isFetching) {
      setIsLoading(false)
      setIsSorting(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSorting, isFetching])

  const onClickSort = (sort) => {
    setSort(sort)
    setIsSorting(true)
  }

  const hasPermissions = () => {
    return (
      permissions?.options?.revision ||
      permissions?.hasShow ||
      permissions?.hasEdit ||
      permissions?.options?.delete
    )
  }

  return (
    <Container className='content'>
      <CardCommon title={translate('ra.entity.promotion')}>
        <List
          {...props}
          component='div'
          actions={false}
        >
          <Datagrid rowClick='show' setSort={onClickSort} isLoading={isLoading}>
            <TextField source='name' label={translate('ra.field.promotion_name')} />
            
            <FunctionField
              label={translate('ra.field.apply_coupon')}
              render={(record) => (record.coupon_based ? translate('ra.message.yes') : translate('ra.message.no'))}
            />

            <FunctionField
              label={translate('ra.field.work_unit')}
              render={(record) => record?.work_unit?.long_name || '-'}
            />

            <FunctionField
              label={translate('ra.field.product_type')}
              render={(record) => record?.product_type?.long_name || '-'}
            />

            <DateField 
              label={translate('ra.field.validity_from')} 
              source='starts_at'
              locales='es-AR'
            />
            
            <DateField 
              label={translate('ra.field.validity_to')} 
              source='ends_at'
              locales='es-AR'
            />

            <TextField source='used' label={translate('ra.field.uses')} />

            <FunctionField
              label={translate('ra.field.stock_total')}
              render={(record) => {
                if (!record.coupons || record.coupons.length === 0) return '-'
                const totalStock = record.coupons.reduce((sum, coupon) => sum + (coupon.usage_limit || 0), 0)
                return totalStock || '-'
              }}
            />

            <FunctionField
              label={translate('ra.field.status')}
              render={(record) => (
                <Chip
                  label={record.is_active ? translate('ra.status.active') : translate('ra.status.inactive')}
                  sx={{
                    backgroundColor: record.is_active ? '#4285F4' : '#F44336',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                  }}
                />
              )}
            />

            {hasPermissions() && (
              <ListViewActionsButtons
                label={translate('ra.action.actions')}
                fieldsName={['name']}
                genderElement='feminine'
                disableRevision
                DeleteButtonComponent={ArchivePromotionButton}
              />
            )}
          </Datagrid>
        </List>
      </CardCommon>
    </Container>
  )
}